<script setup>
const colorMode = useColorMode()

const props = defineProps({
  track: Object,
  indexTrack: Number,
  active: { type: Boolean, default: false }
})

const audioRef = ref(null)

const isPlaying = ref(false)
const currentTime = ref(0)
const duration = ref(0)
const progress = ref(0)

let animationFrameId = null

const stopProgressRaf = () => {
  if (animationFrameId !== null) {
    window.cancelAnimationFrame(animationFrameId)
    animationFrameId = null
  }
}

const startProgressRaf = () => {
  stopProgressRaf()
  const loop = () => {
    const audio = audioRef.value
    if (!audio) return
    currentTime.value = audio.currentTime || 0
    progress.value = duration.value ? (audio.currentTime / duration.value) * 100 : 0
    animationFrameId = window.requestAnimationFrame(loop)
  }
  animationFrameId = window.requestAnimationFrame(loop)
}

const togglePlay = () => {
  const audio = audioRef.value
  if (!audio) return

  if (audio.paused) {
    audio.play()
    isPlaying.value = true
    startProgressRaf()
  } else {
    audio.pause()
    isPlaying.value = false
    stopProgressRaf()
  }
}

const onEnded = () => {
  isPlaying.value = false
  stopProgressRaf()
}

const setDuration = () => {
  const audio = audioRef.value
  if (audio) duration.value = audio.duration || 0
}

const seek = (event) => {
  const container = event.currentTarget
  const rect = container.getBoundingClientRect()
  const clickX = event.offsetX != null ? event.offsetX : (event.clientX - rect.left)
  const width = container.clientWidth || rect.width || 1
  const newTime = (clickX / width) * (duration.value || 0)

  if (audioRef.value) {
    audioRef.value.currentTime = newTime
    if (isPlaying.value) startProgressRaf()
  }
}

const convertStoMs = (seconds = 0) => {
  const m = Math.floor(seconds / 60)
  const s = ('0' + Math.floor(seconds % 60)).slice(-2)
  return `${m} : ${s}`
}

watch(() => props.active, (val, oldVal) => {
  if (val === false && oldVal === true && props.track && props.track.sample) {
    const a = audioRef.value
    if (a) {
      a.pause()
      a.currentTime = 0
    }
    currentTime.value = 0
    isPlaying.value = false
    stopProgressRaf()
  }
})

onBeforeUnmount(() => {
  stopProgressRaf()
})
</script>

<template>
	<div>
		<div
			@click.prevent="track.sample ? togglePlay() : ''"
			class="cursor-pointer xl:text-lg dark:text-light text-dark dark:hover:bg-dark8 hover:bg-lightText px-3 md:px-6 transition-all"
			:class="[active && track.sample ? 'py-8 dark:bg-dark8 bg-lightText' : 'py-2']"
		>
			<div class="flex items-center w-full justify-between">
				<div class="flex items-center">
					<span class="block w-6 md:w-8 top-[2px] relative">{{ indexTrack }}.</span>
					<div v-if="track.sample" class="flex items-center md:mr-5">
						<svg v-if="!isPlaying" class="mx-4 md:mx-5 w-[25px] basis-[25px] flex-shrink-0" xmlns="http://www.w3.org/2000/svg" width="25" height="26" viewBox="0 0 25 26" fill="none">
							<circle cx="12.5" cy="12.5" r="12.5" :fill="colorMode.preference === 'dark' ? '#F5F5F5' : '#1D1D1B'"/>
							<path d="M17 12.5L10 8V17L17 12.5Z" :fill="colorMode.preference === 'dark' ? '#1D1D1B' : '#F5F5F5'" :stroke="colorMode.preference === 'dark' ? '#1D1D1B' : '#F5F5F5'" stroke-width="2" stroke-linecap="square" stroke-linejoin="round"/>
						</svg>
						<svg v-else class="mx-4 md:mx-5 w-[25px] basis-[25px] flex-shrink-0" xmlns="http://www.w3.org/2000/svg" width="25" height="26" viewBox="0 0 25 26" fill="none">
							<circle cx="12.5" cy="13" r="11.5" :stroke="colorMode.preference === 'dark' ? '#F5F5F5' : '#1D1D1B'" stroke-width="2"/>
							<path d="M14 17H15V8H14V17Z" :fill="colorMode.preference === 'dark' ? '#F5F5F5' : '#1D1D1B'" :stroke="colorMode.preference === 'dark' ? '#F5F5F5' : '#1D1D1B'" stroke-width="2" stroke-linecap="square" stroke-linejoin="bevel"/>
							<path d="M10 17H11V8H10V17Z" :fill="colorMode.preference === 'dark' ? '#F5F5F5' : '#1D1D1B'" :stroke="colorMode.preference === 'dark' ? '#F5F5F5' : '#1D1D1B'" stroke-width="2" stroke-linecap="square" stroke-linejoin="bevel"/>
						</svg>
						
					</div>
				</div>
				<div class="min-w-0 truncate pr-2 w-full">
					{{ track.name }}
				</div>
				<div v-if="track.duration" class="whitespace-nowrap">
					{{ convertStoMs(track.duration) }}
				</div>
			</div>
			<div v-if="track.sample" class="collapsible" :class="{ 'is-open pt-6': active }">
				<div class="collapsible-inner px-4 ">
					<div class="w-full relative h-6 dark:bg-[#575755] bg-lightBg8 flex items-center text-xs" @click.stop="seek($event)">
						<span class="absolute z-10 left-6">{{ $t('album.sample-is-playing') }}</span>
						<div class="progress bg-red" :style="{ width: progress + '%' }"></div>
					</div>
					<Button @click.stop :to="track.url" is-small target class="mt-7">{{ $t('album.buy-track') }}</Button>
				</div>
			</div>
		</div>
		<audio
      v-if="track?.sample"
      ref="audioRef"
      :src="track.sample.stream"
      @timeupdate="updateProgress"
      @loadedmetadata="setDuration"
      @ended="onEnded"
    ></audio>
	</div>
</template>

<style lang="scss" scoped>

	.collapsible {
		display: grid;
		grid-template-rows: 0fr;
		transition: grid-template-rows 500ms;

		& > .collapsible-inner {
			overflow: hidden;
		}

		&.is-open {
			grid-template-rows: 1fr;
		}
	}

	.progress {
		height: 100%;
		width: 0%;
		transition: width 0.1s linear;
	}

</style>